<script>
  import Icon from "./Icon.svelte";

  /**
   * Reusable Textarea Component
   * @prop {string} id - Textarea ID
   * @prop {string} label - Label text
   * @prop {string} placeholder - Placeholder text
   * @prop {string} value - Value (bindable)
   * @prop {number} rows - Number of rows
   * @prop {boolean} required - Required field
   * @prop {boolean} disabled - Disabled state
   * @prop {string} error - Error message
   * @prop {string} helpText - Help text
   * @prop {string} icon - Optional icon name (Lucide icon)
   */
  let {
    id = "",
    label = "",
    placeholder = "",
    value = $bindable(""),
    rows = 4,
    required = false,
    disabled = false,
    error = "",
    helpText = "",
    icon = "",
    oninput = undefined,
    onchange = undefined,
  } = $props();
</script>

<div class="form-group" class:has-error={error}>
  {#if label}
    <label for={id} class="form-label">
      {#if icon}<Icon name={icon} size={16} className="field-icon" />{/if}
      {label}
      {#if required}<span class="required">*</span>{/if}
    </label>
  {/if}

  <textarea
    {id}
    class="form-textarea"
    {placeholder}
    bind:value
    {rows}
    {required}
    {disabled}
    aria-invalid={error ? "true" : undefined}
    aria-describedby={error
      ? `${id}-error`
      : helpText
        ? `${id}-help`
        : undefined}
    {oninput}
    {onchange}
  ></textarea>

  {#if error}
    <p id="{id}-error" class="form-error">{error}</p>
  {:else if helpText}
    <p id="{id}-help" class="form-help">{helpText}</p>
  {/if}
</div>

<style>
  .form-group {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .form-label {
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-gray-600);
    letter-spacing: var(--tracking-wide);
  }

  .required {
    color: var(--color-primary-500);
    margin-left: var(--space-1);
  }

  .form-textarea {
    width: 100%;
    padding: var(--space-3) var(--space-4);
    font-size: var(--text-base);
    color: var(--color-gray-800);
    background: var(--glass-bg);
    backdrop-filter: blur(var(--blur-sm));
    -webkit-backdrop-filter: blur(var(--blur-sm));
    border: 1px solid var(--color-gray-200);
    border-radius: var(--radius-lg);
    resize: vertical;
    min-height: 100px;
    transition:
      border-color var(--transition-fast),
      box-shadow var(--transition-fast),
      background var(--transition-fast);
    font-family: inherit;
    line-height: var(--leading-normal);
  }

  .form-textarea::placeholder {
    color: var(--color-gray-400);
  }

  .form-textarea:hover:not(:disabled) {
    border-color: var(--color-gray-300);
    background: var(--glass-bg-dark);
  }

  .form-textarea:focus {
    outline: none;
    border-color: var(--color-primary-400);
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
    background: white;
  }

  .form-textarea:disabled {
    background: var(--color-gray-100);
    color: var(--color-gray-500);
    cursor: not-allowed;
    backdrop-filter: none;
  }

  .has-error .form-textarea {
    border-color: var(--color-error-400);
    background: rgba(254, 242, 242, 0.5);
  }

  .has-error .form-textarea:focus {
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
  }

  .form-error {
    font-size: var(--text-sm);
    color: var(--color-error-600);
    margin: 0;
  }

  .form-help {
    font-size: var(--text-sm);
    color: var(--color-gray-500);
    margin: 0;
  }
</style>
